<template>
    <div>
        <div class="d-flex flex-wrap align-center mb-4" style="gap: 8px;">
            <div class="pa-small pa-muted">
                Posição em {{ data ? formatDateTime(data.generated_at) : "—" }} · só contam adesões aprovadas da versão vigente
            </div>
            <v-spacer></v-spacer>
            <v-btn outlined :loading="loading" @click="load"><v-icon left small>mdi-refresh</v-icon>Atualizar</v-btn>
            <v-btn outlined :loading="busy === 'xlsx'" @click="download('xlsx')"><v-icon left small>mdi-microsoft-excel</v-icon>Planilha</v-btn>
            <v-btn color="primary" depressed :loading="busy === 'pdf'" @click="download('pdf')"><v-icon left small>mdi-file-pdf-box</v-icon>Laudo em PDF</v-btn>
        </div>

        <v-progress-linear v-if="loading && !data" indeterminate color="primary"></v-progress-linear>

        <template v-if="data">
            <div class="pa-card pa-quorum">
                <div class="pa-quorum__icon" :class="data.quorum.approved ? 'pa-quorum__icon--ok' : ''">
                    <v-icon :color="data.quorum.approved ? '#16a34a' : '#71717a'">{{ data.quorum.approved ? "mdi-check-decagram" : "mdi-scale-balance" }}</v-icon>
                </div>
                <div class="flex-grow-1">
                    <div class="pa-section-title">
                        {{ data.quorum.approved ? "Quórum de adesão atingido em todas as classes votantes" : `Quórum atingido em ${data.quorum.classes_reached} de ${data.quorum.classes_voting} classes votantes` }}
                    </div>
                    <div v-if="data.quorum.pending_rates.length" class="pa-section-description" style="color: #b45309;">
                        Há créditos em {{ data.quorum.pending_rates.join(", ") }} sem cotação: o quórum por valor dessas classes fica pendente.
                    </div>
                    <div v-else-if="data.quorum.rates.usd || data.quorum.rates.eur" class="pa-section-description">
                        Cotação do quórum por valor: USD {{ data.quorum.rates.usd || "—" }} · EUR {{ data.quorum.rates.eur || "—" }}{{ data.quorum.rates.date ? ` · ${data.quorum.rates.date}` : "" }}
                    </div>
                </div>
                <div class="pa-kpis">
                    <div v-for="kpi in kpis" :key="kpi.label" class="pa-kpi">
                        <div class="pa-kpi__value">{{ kpi.value }}</div>
                        <div class="pa-kpi__label">{{ kpi.label }}</div>
                    </div>
                </div>
            </div>

            <pa-section title="Quórum por classe" description="Art. 45 c/c art. 56-A. A marca indica 50%; é preciso mais da metade." flush class="mt-4">
                <v-simple-table>
                    <thead>
                        <tr>
                            <th>Classe</th>
                            <th style="min-width: 200px;">Credores</th>
                            <th style="min-width: 240px;">Valor (R$)</th>
                            <th>Exigência</th>
                            <th>Resultado</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in data.classes" :key="row.class_type">
                            <td class="font-weight-medium">{{ row.name }}</td>
                            <td class="py-3">
                                <div class="d-flex justify-space-between pa-small"><span>{{ row.heads_adhered }} de {{ row.heads_total }}<span v-if="row.heads_partial" class="pa-muted"> · {{ row.heads_partial }} parcial</span></span><span class="font-weight-medium">{{ pct(row.heads_pct) }}</span></div>
                                <div class="pa-meter"><div class="pa-meter__fill" :style="{ width: `${Math.min(row.heads_pct, 100)}%` }"></div><span class="pa-meter__mark"></span></div>
                            </td>
                            <td class="py-3">
                                <template v-if="row.value_available">
                                    <div class="d-flex justify-space-between pa-small"><span>{{ money(row.value_adhered_brl) }} de {{ money(row.value_total_brl) }}</span><span class="font-weight-medium">{{ pct(row.value_pct) }}</span></div>
                                    <div v-if="row.requires_amount_majority" class="pa-meter"><div class="pa-meter__fill pa-meter__fill--value" :style="{ width: `${Math.min(row.value_pct, 100)}%` }"></div><span class="pa-meter__mark"></span></div>
                                </template>
                                <span v-else class="pa-small" style="color: #b45309;">Cotação pendente</span>
                            </td>
                            <td class="pa-muted">{{ !row.voting ? "Sem voto" : row.requires_amount_majority ? "Cabeças e valor" : "Cabeças" }}</td>
                            <td>
                                <pa-badge v-if="row.voting" :variant="row.reached ? 'success' : 'neutral'" :text="row.reached ? 'Atingido' : 'Não atingido'" />
                                <span v-else class="pa-muted">—</span>
                            </td>
                        </tr>
                    </tbody>
                </v-simple-table>
            </pa-section>

            <v-row class="mt-1">
                <v-col v-for="chart in currencyCharts" :key="chart.code" cols="12" md="6">
                    <div class="pa-card pa-chart"><highcharts :options="chart.options"></highcharts></div>
                </v-col>
                <v-col cols="12" md="6">
                    <div class="pa-card pa-chart"><highcharts :options="funnelChart"></highcharts></div>
                </v-col>
                <v-col cols="12" md="6">
                    <div class="pa-card pa-chart"><highcharts :options="timelineChart"></highcharts></div>
                </v-col>
            </v-row>

            <pa-section title="Opções de pagamento escolhidas" flush class="mt-4">
                <v-simple-table>
                    <thead><tr><th>Classe</th><th>Opção</th><th>Adesões</th><th>Valor</th></tr></thead>
                    <tbody>
                        <tr v-for="option in data.options" :key="option.option_id">
                            <td class="pa-muted">{{ className(option.class_type) }}</td>
                            <td class="font-weight-medium">{{ option.title }}</td>
                            <td>{{ option.count }}</td>
                            <td>{{ Object.entries(option.amounts).map(([code, value]) => money(value, code)).join(" · ") || "—" }}</td>
                        </tr>
                        <tr v-if="!data.options.length"><td colspan="4" class="pa-muted">Nenhuma opção publicada.</td></tr>
                    </tbody>
                </v-simple-table>
            </pa-section>

            <pa-section v-if="data.nominal" :title="`Aderentes (${data.nominal.length})`" description="CPF/CNPJ mascarado, como na planilha." flush class="mt-4">
                <template #actions>
                    <v-text-field v-model="search" dense outlined hide-details clearable prepend-inner-icon="mdi-magnify" placeholder="Buscar aderente" style="width: 260px;" @input="nominalPage = 1"></v-text-field>
                </template>
                <v-data-table :headers="nominalHeaders" :items="filteredNominal" disable-sort hide-default-footer
                    :page.sync="nominalPage" :items-per-page="nominalPerPage" no-data-text="Nenhuma adesão aprovada">
                    <template v-slot:[`item.name`]="{ item }"><span class="pa-cell-main">{{ item.name }}</span></template>
                    <template v-slot:[`item.registration_masked`]="{ item }"><span class="pa-mono pa-muted">{{ item.registration_masked }}</span></template>
                    <template v-slot:[`item.amount`]="{ item }">{{ money(item.amount, item.currency) }}</template>
                    <template v-slot:[`item.document_code`]="{ item }"><span class="pa-mono pa-muted">{{ item.document_code }}</span></template>
                    <template v-slot:[`item.approved_at`]="{ item }"><span style="white-space: nowrap;">{{ formatDateTime(item.approved_at) }}</span></template>
                </v-data-table>
                <pa-pagination :total="filteredNominal.length" :page.sync="nominalPage" :items-per-page.sync="nominalPerPage" />
            </pa-section>
        </template>
    </div>
</template>

<script>
import axios from "axios";
import { errorMessage, formatDateTime } from "./plan-adhesion.util";
import PaBadge from "./PaBadge.vue";
import PaSection from "./PaSection.vue";
import PaPagination from "./PaPagination.vue";

const FUNNEL_LABELS = {
    awaiting_signature: "Aguardando assinatura",
    submitted: "Enviados",
    in_review: "Em análise (PJ)",
    approved: "Aprovados",
    rejected: "Rejeitados",
    revoked: "Revogados",
    cancelled: "Cancelados",
};
// Graficos neutros (estilo das telas): cinza para o total, cor da marca para o aderido.
const BRAND = { purple: "#150030", team: "#7857a8", lilac: "#a1a1aa", soft: "#e4e4e7" };
const CHART_BASE = {
    chart: { height: 260, style: { fontFamily: "inherit" }, spacing: [16, 16, 12, 12] },
    credits: { enabled: false },
    title: { align: "left", style: { fontSize: "14px", fontWeight: "600", color: "#09090b" } },
    legend: { itemStyle: { fontWeight: "500", color: "#52525b", fontSize: "12px" } },
};

// Painel analitico da Adesao ao Plano (AJ e recuperanda). `urls` traz as rotas
// de cada painel: { analytics, pdf, xlsx }.
export default {
    name: "PlanAdhesionAnalytics",

    components: { PaBadge, PaSection, PaPagination },

    props: {
        urls: { type: Object, required: true },
    },

    data: () => ({
        data: null,
        loading: false,
        busy: null,
        search: "",
        nominalPage: 1,
        nominalPerPage: 10,
        nominalHeaders: [
            { text: "Aderente", value: "name" },
            { text: "CPF/CNPJ", value: "registration_masked" },
            { text: "Classe", value: "class_name" },
            { text: "Valor", value: "amount", align: "end" },
            { text: "Opção", value: "payment_option" },
            { text: "Termo", value: "document_code" },
            { text: "Aprovado em", value: "approved_at" },
        ],
    }),

    computed: {
        filteredNominal() {
            const rows = (this.data && this.data.nominal) || [];
            const term = (this.search || "").trim().toLowerCase();
            if (!term) return rows;
            return rows.filter((row) => [row.name, row.registration_masked, row.document_code, row.class_name, row.payment_option]
                .some((value) => String(value || "").toLowerCase().includes(term)));
        },
        kpis() {
            const summary = this.data.summary;
            return [
                { label: "Credores no QGC consolidado", value: summary.eligible_creditors },
                { label: "Adesões aprovadas", value: summary.approved_terms },
                { label: "Em análise pelo AJ (PJ)", value: summary.in_review },
                { label: "Aguardando assinatura", value: summary.awaiting_signature },
            ];
        },

        // Um grafico por moeda, sem conversao (R13).
        currencyCharts() {
            const codes = [...new Set(this.data.classes.flatMap((row) => row.by_currency.map((bucket) => bucket.code)))];
            return codes.map((code) => {
                const rows = this.data.classes.filter((row) => row.by_currency.some((bucket) => bucket.code === code));
                const bucket = (row) => row.by_currency.find((item) => item.code === code);
                return {
                    code,
                    options: {
                        ...CHART_BASE,
                        chart: { ...CHART_BASE.chart, type: "bar" },
                        title: { ...CHART_BASE.title, text: `Valor por classe — ${code}` },
                        xAxis: { categories: rows.map((row) => row.name) },
                        yAxis: { title: { text: null }, labels: { formatter() { return this.value.toLocaleString("pt-BR"); } } },
                        tooltip: { shared: true, valueDecimals: 2 },
                        plotOptions: { bar: { grouping: false, borderWidth: 0 } },
                        series: [
                            { name: "Total no QGC", data: rows.map((row) => bucket(row).total), color: BRAND.soft },
                            { name: "Aderido", data: rows.map((row) => bucket(row).adhered), color: BRAND.team, pointPadding: 0.25 },
                        ],
                    },
                };
            });
        },

        funnelChart() {
            return {
                ...CHART_BASE,
                chart: { ...CHART_BASE.chart, type: "column" },
                title: { ...CHART_BASE.title, text: "Situação dos termos" },
                legend: { enabled: false },
                xAxis: { categories: this.data.funnel.map((row) => FUNNEL_LABELS[row.status] || row.status) },
                yAxis: { title: { text: null }, allowDecimals: false },
                series: [{ name: "Termos", data: this.data.funnel.map((row) => row.count), color: BRAND.lilac }],
            };
        },

        timelineChart() {
            return {
                ...CHART_BASE,
                chart: { ...CHART_BASE.chart, type: "area" },
                title: { ...CHART_BASE.title, text: "Adesões aprovadas (acumulado)" },
                legend: { enabled: false },
                xAxis: { categories: this.data.timeline.map((row) => row.date.split("-").reverse().slice(0, 2).join("/")) },
                yAxis: { title: { text: null }, allowDecimals: false },
                series: [{ name: "Acumulado", data: this.data.timeline.map((row) => row.cumulative), color: BRAND.team, fillOpacity: 0.15 }],
            };
        },
    },

    created() {
        this.load();
    },

    methods: {
        formatDateTime,

        pct(value) {
            return value === null || value === undefined ? "—" : `${Number(value).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`;
        },

        money(value, code = "BRL") {
            if (value === null || value === undefined) return "—";
            return Number(value).toLocaleString("pt-BR", { style: "currency", currency: code });
        },

        className(type) {
            const row = this.data.classes.find((item) => item.class_type === type);
            return row ? row.name : `Classe ${type}`;
        },

        async load() {
            this.loading = true;
            try {
                const result = await axios.get(this.urls.analytics);
                this.data = result.data.content;
            } catch (error) {
                this.$dialog.notify.error(errorMessage(error), { position: "top-right", timeout: 5000 });
            } finally {
                this.loading = false;
            }
        },

        async download(kind) {
            this.busy = kind;
            try {
                const result = await axios.get(kind === "pdf" ? this.urls.pdf : this.urls.xlsx);
                const { base64, mime_type: mimeType, file_name: fileName } = result.data.content;
                const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
                const url = URL.createObjectURL(new Blob([bytes], { type: mimeType }));
                const link = document.createElement("a");
                link.href = url;
                link.download = fileName;
                link.click();
                setTimeout(() => URL.revokeObjectURL(url), 60000);
            } catch (error) {
                this.$dialog.notify.error(errorMessage(error), { position: "top-right", timeout: 5000 });
            } finally {
                this.busy = null;
            }
        },
    },
};
</script>

